import type { FC } from 'react'
import React from 'react'
// Types
import type { Component, CreateTargetDTO } from 'types/components'
import type { OrganizationCoreSetting } from 'types/organization'
import type { Period } from 'types/periods'

// Icons
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Settings } from '@mui/icons-material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { uniq } from 'lodash'

// MUI Components
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'

// Custom Components & Hooks
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import ComponentField from 'components/ComponentField'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import TrueFalseSelector from 'components/TrueFalseSelector'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { postWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useFetchOrganizationComponents } from 'hooks/useComponents'
import useLocalStorage from 'hooks/useLocalStorage'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'

import InfoIcon from '@mui/icons-material/Info'
import { usePeriods } from 'hooks/usePeriods'
import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ComponentSourceType, ComponentType, CompositeFunction, CompositeType, ProrationMode } from 'types/components'
import { ThemeMode } from 'types/config'
import { createIdLabelMap, createPeriodToRootPeriodMap } from 'types/periods'
import { getOrganizationLabelMap } from 'types/user-profile'

interface TargetMeta {
  targetName: string
  orgLevel: boolean
  targetMin: number | null
  targetMax: number | null
  targetDefault: number | boolean | string | null
  targetFormat: ComponentType
  isExpanded: boolean
  restrictedVisibility: boolean
  prorationMode: ProrationMode
  sourceType: ComponentSourceType
  compositeFunction: CompositeFunction
  compositeType: CompositeType
  lowerIsBetter: boolean
  numericDecimalPlaces?: number
  precision?: number
}

interface CompositeValue {
  label: string
  description: string
  target: string
  componentType?: ComponentType
}

interface CompositeValues {
  values: CompositeValue[]
}

const DEFAULT_TARGET_META: TargetMeta = {
  targetName: '',
  orgLevel: true,
  targetMin: null,
  targetMax: null,
  targetDefault: null,
  targetFormat: ComponentType.COMPONENT_TYPE_INTEGER,
  isExpanded: false,
  restrictedVisibility: false,
  prorationMode: ProrationMode.PRORATION_MODE_NONE,
  sourceType: ComponentSourceType.COMPONENT_SOURCE_MANUAL,
  compositeFunction: CompositeFunction.COMPOSITE_FUNCTION_NONE,
  compositeType: CompositeType.COMPOSITE_TYPE_SIMPLE,
  lowerIsBetter: false,
  numericDecimalPlaces: 2,
  precision: 2,
}

const getTypeDisplayName = (type: ComponentType) => {
  switch (type) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
      return 'Integer'
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return 'Currency'
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return 'Percentage'
    case ComponentType.COMPONENT_TYPE_BOOL:
      return 'Boolean'
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return 'Float'
    default:
      return type
  }
}

const CreateTargets: FC = () => {
  const theme = useTheme()
  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.dark

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const { purcentAppState } = useGlobalState()

  const [selectedOrganization, setSelectedOrganization] = useLocalStorage<string>(
    'createTargets-selectedOrganization',
    '',
    profile?.user?.id
  )
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'createTargets-selectedPeriods',
    [],
    profile?.user?.id
  )
  const [targetMetas, setTargetMetas] = useLocalStorage<TargetMeta[]>(
    'createTargets-targetMetas',
    [{ ...DEFAULT_TARGET_META }],
    profile?.user?.id
  )
  const [compositeValues, setCompositeValues] = useLocalStorage<Record<number, CompositeValues>>(
    'createTargets-compositeValues',
    {},
    profile?.user?.id
  )

  const [isReviewMode, setIsReviewMode] = useState(false)
  const [isReviewLoading, setIsReviewLoading] = useState(false)
  const [isReviewClicked, setIsReviewClicked] = useState(false)
  const [reviewTargets, setReviewTargets] = useState<CreateTargetDTO[]>([])
  const [availableLabels, setAvailableLabels] = useState<string[]>(['Revenue', 'Cost', 'Profit'])
  const [componentLabelMap, setComponentLabelMap] = useState<Record<string, Component>>({})

  const { periods, isLoadingPeriods } = usePeriods(
    token!,
    purcentAppState?.selectedOrganization?.rootOrganizationId || ''
  )
  const { coreSettings, isLoading: isLoadingCoreSettings } = useFetchOrganizationCoreSettings(
    selectedOrganization ? [selectedOrganization] : [],
    token!
  )

  const { components, isLoading: isLoadingComponents } = useFetchOrganizationComponents(
    token!,
    selectedOrganization || '',
    null
  )

  const [coreSettingsMap, setCoreSettingsMap] = useState<Record<string, OrganizationCoreSetting>>({})

  const organizationLabelMap = useMemo(() => (profile ? getOrganizationLabelMap(profile) : {}), [profile])

  const periodLabelMap = useMemo(() => {
    if (!periods?.periods) return {}
    return periods.periods.reduce((acc: Record<string, string>, period) => {
      const idLabelMap = createIdLabelMap(period)
      Object.entries(idLabelMap).forEach(([id, label]) => {
        const newLabel = label.includes(period.label) ? label : `${label} (${period.label})`
        acc[id] = newLabel
      })
      return acc
    }, {})
  }, [periods])

  const rootPeriodMap = useMemo(() => {
    if (isLoadingPeriods || !periods?.periods) return {}
    return createPeriodToRootPeriodMap(periods.periods)
  }, [periods])

  // Update available labels if `labels` prop changes
  useEffect(() => {
    if (!isLoadingComponents) {
      const activeComponents = components.filter((c) => !c.isArchived)
      setAvailableLabels(uniq(activeComponents.map((component) => component.label)))
      setComponentLabelMap(
        activeComponents.reduce(
          (acc, component) => {
            acc[component.label] = component
            return acc
          },
          {} as Record<string, Component>
        )
      )
    }
  }, [components])

  // Filter available labels to exclude already selected components
  const getFilteredLabels = (currentIndex: number) => {
    const selectedLabels = targetMetas
      .map((meta, index) => (index !== currentIndex ? meta.targetName : null))
      .filter((label): label is string => label !== null && label !== '')

    return availableLabels.filter((label) => !selectedLabels.includes(label))
  }

  // Build core settings map
  useEffect(() => {
    if (coreSettings && coreSettings.length > 0) {
      const newCoreSettingsMap = coreSettings.reduce(
        (acc, setting) => {
          acc[setting.organizationId] = setting
          return acc
        },
        {} as Record<string, OrganizationCoreSetting>
      )
      setCoreSettingsMap(newCoreSettingsMap)
    }
  }, [coreSettings])

  // Reset review states when the browser back button is pressed
  useEffect(() => {
    const handlePopState = () => {
      console.log('log 4')
      setIsReviewClicked(false)
      setIsReviewLoading(false)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const handleTargetChange = (index: number, field: keyof TargetMeta, value: any) => {
    const newMetas = [...targetMetas]
    newMetas[index] = { ...newMetas[index], [field]: value }

    // If changing the label, check if it exists in componentLabelMap
    if (field === 'targetName') {
      const existingComponent = componentLabelMap[value]
      if (existingComponent) {
        // Sync all relevant fields with the existing component
        newMetas[index].targetFormat = existingComponent.componentType
        newMetas[index].targetMin = existingComponent.targetMin ? Number(existingComponent.targetMin) : null
        newMetas[index].targetMax = existingComponent.targetMax ? Number(existingComponent.targetMax) : null
        newMetas[index].sourceType = existingComponent.sourceType
        newMetas[index].orgLevel = existingComponent.isOrganizationLevel
        newMetas[index].numericDecimalPlaces = existingComponent.numericDecimalPlaces
        newMetas[index].precision = existingComponent.precision
        console.log('existingComponent', existingComponent)
        try {
          if (existingComponent.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
            const sourceInfo = existingComponent.sourceInfo ? (existingComponent.sourceInfo as string) : ''
            const parsedSourceInfo = sourceInfo ? JSON.parse(sourceInfo) : {}
            const compositeFunction = parsedSourceInfo.compositeFunction || CompositeFunction.COMPOSITE_FUNCTION_NONE
            const compositeType = parsedSourceInfo.compositeType || CompositeType.COMPOSITE_TYPE_SIMPLE
            newMetas[index].compositeFunction = compositeFunction
            newMetas[index].compositeType = compositeType
          } else {
            newMetas[index].compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE
            newMetas[index].compositeType = CompositeType.COMPOSITE_TYPE_SIMPLE
          }
        } catch {
          newMetas[index].compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE
          newMetas[index].compositeType = CompositeType.COMPOSITE_TYPE_SIMPLE
        }
        newMetas[index].lowerIsBetter = existingComponent.lowerIsBetter
      }
    } else if (field === 'targetFormat') {
      // Handle boolean type changes
      if (value === ComponentType.COMPONENT_TYPE_BOOL) {
        // Close advanced settings for boolean
        newMetas[index].isExpanded = false
        // Set appropriate default value for boolean
        newMetas[index].targetDefault = false
        // Clear min/max values for boolean
        newMetas[index].targetMin = null
        newMetas[index].targetMax = null
      } else if (newMetas[index].targetFormat === ComponentType.COMPONENT_TYPE_BOOL) {
        // Switching FROM boolean to another type - set numeric default
        newMetas[index].targetDefault = 0
      }
    } else if (field === 'sourceType') {
      if (value === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        // Initialize composite values when switching to composite
        const initialCompositeValues = { values: [] }
        setCompositeValues((prev) => ({
          ...prev,
          [index]: initialCompositeValues,
        }))
        // Set a proper default value based on component type instead of JSON string
        if (newMetas[index].targetFormat === ComponentType.COMPONENT_TYPE_BOOL) {
          newMetas[index].targetDefault = false
        } else {
          newMetas[index].targetDefault = 0
        }
      } else {
        // Reset to default when switching away from composite
        if (newMetas[index].targetFormat === ComponentType.COMPONENT_TYPE_BOOL) {
          newMetas[index].targetDefault = false
        } else {
          newMetas[index].targetDefault = 0
        }
        newMetas[index].compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE
        setCompositeValues((prev) => {
          const newValues = { ...prev }
          delete newValues[index]
          return newValues
        })
      }
    } else if (newMetas[index].sourceType !== ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
      newMetas[index].compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE
    }
    setTargetMetas(newMetas)
  }

  const handleUpdateCompositeValue = (
    targetIndex: number,
    valueIndex: number,
    field: 'label' | 'description' | 'target',
    newValue: string
  ) => {
    if (!compositeValues[targetIndex]) return

    const newValues = {
      ...compositeValues,
      [targetIndex]: {
        values: compositeValues[targetIndex].values.map((item: CompositeValue, i: number) =>
          i === valueIndex ? { ...item, [field]: newValue } : item
        ),
      },
    }
    setCompositeValues(newValues)
  }

  const handleUpdateCompositeRowComponentType = (targetIndex: number, valueIndex: number, newType: ComponentType) => {
    if (!compositeValues[targetIndex]) return

    const newValues = {
      ...compositeValues,
      [targetIndex]: {
        values: compositeValues[targetIndex].values.map((item: CompositeValue, i: number) =>
          i === valueIndex ? { ...item, componentType: newType } : item
        ),
      },
    }
    setCompositeValues(newValues)
  }

  const handleAddCompositeValue = (index: number) => {
    const currentValues = compositeValues[index] || { values: [] }
    const newCompositeValues = {
      ...compositeValues,
      [index]: {
        values: [
          ...currentValues.values,
          {
            label: `Value ${currentValues.values.length + 1}`,
            description: 'Enter description',
            target: '0',
            componentType: targetMetas[index].targetFormat,
          },
        ],
      },
    }
    setCompositeValues(newCompositeValues)
  }

  const handleRemoveCompositeValue = (targetIndex: number, valueIndex: number) => {
    if (!compositeValues[targetIndex]) return

    const newValues = {
      ...compositeValues,
      [targetIndex]: {
        values: compositeValues[targetIndex].values.filter((_, i) => i !== valueIndex),
      },
    }
    setCompositeValues(newValues)
  }

  const handleProrationModeChange = (index: number, value: ProrationMode) => {
    handleTargetChange(index, 'prorationMode', value)
  }

  const addTarget = () => {
    setTargetMetas((prev) => [...prev, { ...DEFAULT_TARGET_META }])
  }

  const removeTarget = (index: number) => {
    setTargetMetas((prev) => prev.filter((_, i) => i !== index))
  }

  const handleOrganizationChange = useCallback((org: string) => {
    setSelectedOrganization(org)
  }, [])

  const handlePeriodChange = useCallback((periods: string[]) => {
    startTransition(() => setSelectedPeriods(periods))
  }, [])

  const handleExpand = (index: number) => {
    handleTargetChange(index, 'isExpanded', !targetMetas[index].isExpanded)
  }

  const isTargetMetasValid = useCallback(() => {
    return targetMetas.every((targetMeta) => targetMeta.targetName !== '' && targetMeta.targetDefault !== null)
  }, [targetMetas])

  const createReviewTargets = useCallback(() => {
    if (!purcentAppState?.selectedOrganization?.rootOrganizationId) return
    const rootOrgId = purcentAppState.selectedOrganization.rootOrganizationId
    let combinedTargets: CreateTargetDTO[] = []

    // Generate targets for selected organization and selected period combination
    if (selectedOrganization) {
      selectedPeriods.forEach((periodId) => {
        targetMetas.forEach((target, index) => {
          let calculatedPeriodValue = 0
          if (target.targetFormat !== ComponentType.COMPONENT_TYPE_BOOL) {
            calculatedPeriodValue = Number.parseFloat(target.targetDefault?.toString() ?? '0')
          }

          const rootPeriodId = rootPeriodMap[periodId]
          const newTarget: CreateTargetDTO = {
            rootOrganizationId: rootOrgId,
            organizationId: selectedOrganization,
            rootPeriodId,
            periodId: periodId,
            label: target.targetName,
            isOrganizationLevel: target.orgLevel,
            isRestricted: false,
            componentType: target.targetFormat,
            targetMin: target.targetMin?.toString() ?? '',
            targetMax: target.targetMax?.toString() ?? '',
            value:
              target.targetFormat !== ComponentType.COMPONENT_TYPE_BOOL
                ? calculatedPeriodValue.toString()
                : (target.targetDefault?.toString() ?? ''),
            sourceType: target.sourceType,
            sourceInfo: JSON.stringify({
              compositeFunction:
                target.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
                  ? target.compositeFunction
                  : CompositeFunction.COMPOSITE_FUNCTION_NONE,
            }),
            lowerIsBetter: target.lowerIsBetter,
          }

          if (target.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
            // Build compositeTargetValues array
            const compositeRows =
              compositeValues[index]?.values.map((item, i) => ({
                label: item.label,
                description: item.description,
                target: item.target,
                componentType: item.componentType,
              })) || []

            newTarget.value = JSON.stringify({
              value: calculatedPeriodValue.toString(),
              compositeTargetValues: compositeRows,
            })
          } else {
            // For non-composite targets, use the appropriate value based on type
            if (target.targetFormat === ComponentType.COMPONENT_TYPE_BOOL) {
              newTarget.value = target.targetDefault?.toString() ?? ''
            } else {
              newTarget.value = calculatedPeriodValue.toString()
            }
          }

          combinedTargets.push(newTarget)
        })
      })
    }

    // Create breadth-first ordered list of period IDs for sorting
    const getPeriodSortOrder = () => {
      if (!periods?.periods) return {}

      const sortOrder: Record<string, number> = {}
      let orderIndex = 0

      // Breadth-first traversal through period hierarchy
      const queue: Period[] = [...periods.periods]

      while (queue.length > 0) {
        const currentPeriod = queue.shift()!
        sortOrder[currentPeriod.id!] = orderIndex++

        // Add children to queue for next level
        if (currentPeriod.children) {
          queue.push(...currentPeriod.children)
        }
      }

      return sortOrder
    }

    const periodSortOrder = getPeriodSortOrder()

    const sortedTargets = combinedTargets.sort((a, b) => {
      // First sort by organization
      const orgComparison =
        organizationLabelMap[a.organizationId]?.localeCompare(organizationLabelMap[b.organizationId]) || 0
      if (orgComparison !== 0) return orgComparison

      // Then sort by period using breadth-first order
      const orderA = periodSortOrder[a.periodId] ?? Number.MAX_SAFE_INTEGER
      const orderB = periodSortOrder[b.periodId] ?? Number.MAX_SAFE_INTEGER
      const periodComparison = orderA - orderB
      if (periodComparison !== 0) return periodComparison

      // Finally sort by target name
      return a.label.localeCompare(b.label)
    })

    setReviewTargets(sortedTargets)
    setIsReviewMode(true)
    setIsReviewLoading(false)
  }, [
    purcentAppState,
    selectedOrganization,
    selectedPeriods,
    targetMetas,
    compositeValues,
    rootPeriodMap,
    organizationLabelMap,
    periodLabelMap,
    periods,
  ])

  const handleReview = () => {
    setIsReviewClicked(true)
    setIsReviewLoading(true)
    // Delay execution to ensure UI updates
    setTimeout(() => {
      createReviewTargets()
    }, 0)
  }

  const handleReviewTargetChange = (index: number, value: string) => {
    const updated = [...reviewTargets]
    updated[index].value = value
    setReviewTargets(updated)
  }

  const handleCreateTargets = () => {
    postWithToken('components/targets', token!, { targets: reviewTargets })
      .then(() => {
        enqueueSnackbar('Targets created successfully', { variant: 'success' })
        // Reset form
        setIsReviewMode(false)
        setSelectedOrganization('')
        setSelectedPeriods([])
        setTargetMetas([{ ...DEFAULT_TARGET_META }])
        setCompositeValues({})
      })
      .catch((error) => {
        const errorMessage = error.message || 'Failed to create targets'
        enqueueSnackbar(errorMessage, { variant: 'error' })
      })
      .finally(() => {
        startTransition(() => navigate('/admin/components/targets'))
      })
  }

  const handleBackFromReview = () => {
    setIsReviewMode(false)
    setIsReviewClicked(false)
  }

  const handleClear = () => {
    setSelectedOrganization('')
    setSelectedPeriods([])
    setTargetMetas([{ ...DEFAULT_TARGET_META }])
    setCompositeValues({})
    setIsReviewMode(false)
    setIsReviewClicked(false)
  }

  const renderInputField = (index: number, field: keyof TargetMeta, label: string) => {
    const targetMeta = targetMetas[index]
    const value = targetMeta[field] ?? ''
    const orgSetting = coreSettingsMap[selectedOrganization] ?? { currency: 'USD', currencyDecimals: 0 }

    return (
      <ComponentField
        id={`target-${index}-${String(field)}`}
        componentType={targetMeta.targetFormat}
        label={label}
        value={value}
        onChange={(newVal) => handleTargetChange(index, field, newVal)}
        name={`target-${index}-${String(field)}`}
        currency={orgSetting.currency}
        currencyDecimals={orgSetting.currencyDecimals}
        decimalPlaces={targetMeta.precision ?? 2}
        variant="outlined"
        shrink
        pill
        style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
      />
    )
  }

  const renderOrgPeriodConfig = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h4" gutterBottom>
        Select Team & Periods for Targets
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {isLoadingProfile ? (
            <CircularProgress />
          ) : (
            <SingleOrganizationSelector
              userProfile={profile!}
              selectedOrganizationId={selectedOrganization}
              handleChange={handleOrganizationChange}
              label="Select Team *"
              labelBackgroundColor={theme.palette.background.paper}
              disableAutoSelection={false}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {isLoadingPeriods || !periods ? (
            <CircularProgress />
          ) : (
            <MultiPeriodSelector
              periods={periods?.periods || []}
              selectedPeriods={selectedPeriods}
              handleChange={handlePeriodChange}
              label="Select Periods *"
              disableMultiRootSelection
              labelBackgroundColor={theme.palette.background.paper}
              disableClosed
              autoSelectDescendants={false}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  )

  const renderTargetMetaConfig = () => (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Targets
      </Typography>
      <Grid container spacing={3}>
        {targetMetas.map((targetMeta, index) => (
          <Grid container size={{ xs: 12 }} spacing={2} key={`target-meta-${index}`}>
            <Grid size={{ xs: 12, sm: 3 }}>
              {isLoadingComponents ? (
                <CircularProgress />
              ) : (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                    Component *
                  </InputLabel>
                  <Select
                    value={targetMeta.targetName}
                    onChange={(e) => handleTargetChange(index, 'targetName', e.target.value)}
                    variant="outlined"
                    style={{ borderRadius: '21px' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          marginTop: 8,
                        },
                      },
                    }}
                  >
                    {getFilteredLabels(index).map((label) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                  Type *
                </InputLabel>
                <Select
                  value={String(targetMeta.orgLevel)}
                  onChange={(e) => handleTargetChange(index, 'orgLevel', e.target.value === 'true')}
                  variant="outlined"
                  style={{ borderRadius: '21px' }}
                  disabled={!!componentLabelMap[targetMeta.targetName]}
                >
                  <MenuItem value="true">Team</MenuItem>
                  <MenuItem value="false">Individual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                  Format *
                </InputLabel>
                <Select
                  value={targetMeta.targetFormat}
                  onChange={(e) => handleTargetChange(index, 'targetFormat', e.target.value as ComponentType)}
                  variant="outlined"
                  style={{ borderRadius: '21px' }}
                  disabled={!!componentLabelMap[targetMeta.targetName]}
                >
                  <MenuItem value={ComponentType.COMPONENT_TYPE_INTEGER}>Integer</MenuItem>
                  <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>Currency</MenuItem>
                  <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>Percentage</MenuItem>
                  <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                  <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                  Source Type *
                </InputLabel>
                <Select
                  key={`source-type-${index}`}
                  value={targetMeta.sourceType}
                  onChange={(e) => {
                    handleTargetChange(index, 'sourceType', e.target.value as ComponentSourceType)
                  }}
                  style={{ borderRadius: '21px' }}
                  disabled={!!componentLabelMap[targetMeta.targetName]}
                >
                  <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_MANUAL}>Standard</MenuItem>
                  <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_COMPOSITE}>Composite</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>{renderInputField(index, 'targetDefault', 'Value *')}</Grid>
            <Grid
              size={{ xs: 12, sm: 1 }}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}
            >
              <IconButton
                onClick={() => handleExpand(index)}
                disabled={targetMeta.targetFormat === ComponentType.COMPONENT_TYPE_BOOL}
              >
                <Settings />
              </IconButton>
              <IconButton onClick={() => removeTarget(index)}>
                <DeleteOutlined />
              </IconButton>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Collapse in={targetMeta.isExpanded && targetMeta.targetFormat !== ComponentType.COMPONENT_TYPE_BOOL}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: theme.palette.grey[100] }}>
                  {/* Composite Values Table */}
                  {targetMeta.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && (
                    <Box>
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h5">Composite Values</Typography>
                            <Button
                              startIcon={<PlusOutlined />}
                              size="small"
                              onClick={() => handleAddCompositeValue(index)}
                            >
                              Add Value
                            </Button>
                          </Box>
                          {targetMeta.compositeType === CompositeType.COMPOSITE_TYPE_SIMPLE && (
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InfoIcon fontSize="small" color="secondary" />
                              <Typography variant="body2" color="text.secondary">
                                <strong>Note:</strong> For simple composite types, targets are used for display purposes
                                only and are not used in calculations.
                              </Typography>
                            </Box>
                          )}
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width="20%">Label</TableCell>
                                  <TableCell width="40%">Description</TableCell>
                                  <TableCell width="15%">Type</TableCell>
                                  <TableCell width="25%">Target</TableCell>
                                  <TableCell width="10%" align="center">
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {compositeValues[index]?.values.map((item, valueIndex) => (
                                  <TableRow key={valueIndex}>
                                    <TableCell>
                                      <TextField
                                        value={item.label}
                                        onChange={(e) =>
                                          handleUpdateCompositeValue(index, valueIndex, 'label', e.target.value)
                                        }
                                        fullWidth
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        value={item.description}
                                        onChange={(e) =>
                                          handleUpdateCompositeValue(index, valueIndex, 'description', e.target.value)
                                        }
                                        fullWidth
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Select
                                        value={item.componentType || targetMetas[index].targetFormat}
                                        onChange={(e) =>
                                          handleUpdateCompositeRowComponentType(
                                            index,
                                            valueIndex,
                                            e.target.value as ComponentType
                                          )
                                        }
                                        style={{ minWidth: 180, maxWidth: 220, width: '100%', borderRadius: '21px' }}
                                        disabled={
                                          targetMetas[index].targetFormat !== ComponentType.COMPONENT_TYPE_PERCENTAGE
                                        }
                                      >
                                        <MenuItem value={ComponentType.COMPONENT_TYPE_INTEGER}>Integer</MenuItem>
                                        <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>Currency</MenuItem>
                                        <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>Percentage</MenuItem>
                                        <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                                        <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell>
                                      <ComponentField
                                        componentType={item.componentType || targetMetas[index].targetFormat}
                                        label=""
                                        value={item.target}
                                        onChange={(value) =>
                                          handleUpdateCompositeValue(index, valueIndex, 'target', value)
                                        }
                                        name={`target-${index}-composite-${valueIndex}`}
                                        currency={coreSettingsMap[selectedOrganization]?.currency || 'USD'}
                                        currencyDecimals={coreSettingsMap[selectedOrganization]?.currencyDecimals || 0}
                                        decimalPlaces={targetMetas[index].precision ?? 2}
                                        style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                                      />
                                    </TableCell>
                                    <TableCell width="10%" align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveCompositeValue(index, valueIndex)}
                                      >
                                        <DeleteOutlined />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {(!compositeValues[index] || compositeValues[index].values.length === 0) && (
                                  <TableRow>
                                    <TableCell colSpan={5} align="center">
                                      <Typography variant="body2" color="textSecondary">
                                        No values added yet. Click "Add Value" to begin.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Grid>
                      <Divider sx={{ mt: 4, mb: 3 }} />
                    </Box>
                  )}

                  <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                    Advanced Settings (when creating new components)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ComponentField
                        id={`target-${index}-targetMin`}
                        componentType={targetMeta.targetFormat}
                        label="Target Min"
                        value={targetMeta.targetMin ?? ''}
                        onChange={(newVal) => handleTargetChange(index, 'targetMin', newVal)}
                        name={`target-${index}-targetMin`}
                        currency={coreSettingsMap[selectedOrganization]?.currency || 'USD'}
                        currencyDecimals={coreSettingsMap[selectedOrganization]?.currencyDecimals || 0}
                        decimalPlaces={targetMeta.precision ?? 2}
                        variant="outlined"
                        shrink
                        pill
                        disabled={!!componentLabelMap[targetMeta.targetName]}
                        style={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ComponentField
                        id={`target-${index}-targetMax`}
                        componentType={targetMeta.targetFormat}
                        label="Target Max"
                        value={targetMeta.targetMax ?? ''}
                        onChange={(newVal) => handleTargetChange(index, 'targetMax', newVal)}
                        name={`target-${index}-targetMax`}
                        currency={coreSettingsMap[selectedOrganization]?.currency || 'USD'}
                        currencyDecimals={coreSettingsMap[selectedOrganization]?.currencyDecimals || 0}
                        decimalPlaces={targetMeta.precision ?? 2}
                        variant="outlined"
                        shrink
                        pill
                        disabled={!!componentLabelMap[targetMeta.targetName]}
                        style={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.grey[100] }}>
                          Lower Is Better
                        </InputLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Select
                            value={targetMeta.lowerIsBetter}
                            onChange={(e) => handleTargetChange(index, 'lowerIsBetter', e.target.value === 'true')}
                            variant="outlined"
                            style={{ borderRadius: '21px', width: '100%' }}
                            disabled={!!componentLabelMap[targetMeta.targetName]}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                          <Tooltip
                            title="Enable this when a lower value is considered better performance. For example, 'Number of Errors' or 'Response Time' where lower values indicate better results."
                            placement="right"
                          >
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                          </Tooltip>
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.grey[100] }}>
                          Composite Function
                        </InputLabel>
                        <Select
                          key={`composite-function-${index}`}
                          value={targetMeta.compositeFunction}
                          onChange={(e) =>
                            handleTargetChange(index, 'compositeFunction', e.target.value as CompositeFunction)
                          }
                          style={{ borderRadius: '21px' }}
                          disabled={
                            targetMeta.sourceType !== ComponentSourceType.COMPONENT_SOURCE_COMPOSITE ||
                            !!componentLabelMap[targetMeta.targetName]
                          }
                        >
                          <MenuItem disabled value={CompositeFunction.COMPOSITE_FUNCTION_NONE}>
                            None
                          </MenuItem>
                          <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_AVERAGE}>Average</MenuItem>
                          <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_MEDIAN}>Median</MenuItem>
                          <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_COUNT}>Count</MenuItem>
                          <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_STDDEV}>Standard Deviation</MenuItem>
                          <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_SUM}>Sum</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.grey[100] }}>
                          Proration Mode
                        </InputLabel>
                        <Select
                          key={`proration-mode-${index}`}
                          value={
                            targetMeta.targetFormat === ComponentType.COMPONENT_TYPE_BOOL
                              ? ProrationMode.PRORATION_MODE_NONE
                              : targetMeta.prorationMode
                          }
                          variant="outlined"
                          onChange={(e) => handleProrationModeChange(index, e.target.value as ProrationMode)}
                          fullWidth
                          disabled={targetMeta.targetFormat === ComponentType.COMPONENT_TYPE_BOOL}
                          style={{ borderRadius: '21px' }}
                        >
                          <MenuItem value={ProrationMode.PRORATION_MODE_NONE}>None</MenuItem>
                          <MenuItem value={ProrationMode.PRORATION_MODE_AMORTIZE}>Amortize</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Collapse>
            </Grid>
          </Grid>
        ))}
        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          <IconButton color="primary" onClick={addTarget} sx={{ width: '250px' }}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <PlusOutlined style={{ marginRight: 8 }} />
              {targetMetas.length > 0 ? 'Add Another Target' : 'Add Target'}
            </Box>
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  )

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})

  const renderReviewConfig = (
    expandedRows: Record<number, boolean>,
    setExpandedRows: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
  ) => {
    if (isLoadingCoreSettings) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      )
    }

    const handleRemoveReviewTarget = (index: number) => {
      setReviewTargets((prev) => prev.filter((_, i) => i !== index))
    }

    return (
      <>
        <Typography variant="h4" gutterBottom>
          Review Targets
        </Typography>
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table stickyHeader size="small" aria-label="review-targets-table">
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  <TableCell width="18%">Organization</TableCell>
                  <TableCell width="14%">Period</TableCell>
                  <TableCell width="22%">Name</TableCell>
                  <TableCell width="12%">Type</TableCell>
                  <TableCell width="18%">Value</TableCell>
                  <TableCell width="8%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviewTargets.map((target, index) => {
                  const orgSettings = coreSettingsMap[target.organizationId] || {
                    currency: 'USD',
                    currencyDecimals: 0,
                    percentageDecimals: 2,
                  }
                  const targetMeta = targetMetas.find((meta) => meta.targetName === target.label)
                  const isComposite = targetMeta?.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE

                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={{ py: 3, '&:hover': { backgroundColor: hoverBackgroundColor } }}>
                        <TableCell>{organizationLabelMap[target.organizationId]}</TableCell>
                        <TableCell>{periodLabelMap[target.periodId]}</TableCell>
                        <TableCell>{target.label}</TableCell>
                        <TableCell>{target.isOrganizationLevel ? 'Team' : 'Individual'}</TableCell>
                        <TableCell>
                          {isComposite ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ComponentField
                                componentType={targetMeta?.targetFormat as ComponentType}
                                label=""
                                value={(() => {
                                  try {
                                    const compositeData = JSON.parse(target.value)
                                    return compositeData.value
                                  } catch {
                                    return ''
                                  }
                                })()}
                                onChange={(value) => {
                                  // Update the top-level value in the composite JSON
                                  let compositeData: any = {}
                                  try {
                                    compositeData = JSON.parse(target.value)
                                  } catch {}
                                  compositeData.value = value
                                  const updated = [...reviewTargets]
                                  updated[index].value = JSON.stringify(compositeData)
                                  setReviewTargets(updated)
                                }}
                                name={`review-target-${index}-composite-main`}
                                currency={orgSettings.currency}
                                currencyDecimals={orgSettings.currencyDecimals}
                                decimalPlaces={targetMeta?.precision ?? 2}
                                style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }))}
                                sx={{ ml: 1 }}
                              >
                                <ExpandMore
                                  style={{
                                    transform: expandedRows[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: '0.2s',
                                  }}
                                />
                              </IconButton>
                            </Box>
                          ) : targetMeta?.targetFormat === ComponentType.COMPONENT_TYPE_BOOL ? (
                            <TrueFalseSelector
                              value={target.value === 'true'}
                              onChange={(value) =>
                                handleReviewTargetChange(index, value === null ? '' : value.toString())
                              }
                              name={`review-target-${index}`}
                              variant="outlined"
                              pill
                              shrink
                              style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                            />
                          ) : (
                            <ComponentField
                              componentType={targetMeta?.targetFormat as ComponentType}
                              label=""
                              value={target.value}
                              onChange={(value) => handleReviewTargetChange(index, value)}
                              name={`review-target-${index}`}
                              currency={orgSettings.currency}
                              currencyDecimals={orgSettings.currencyDecimals}
                              decimalPlaces={targetMeta?.precision ?? 2}
                              style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleRemoveReviewTarget(index)}>
                            <DeleteOutlined />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {isComposite && expandedRows[index] && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Collapse in={expandedRows[index]}>
                              {(() => {
                                let compositeRows: CompositeValue[] = []
                                let compositeData: any = {}
                                try {
                                  compositeData = JSON.parse(target.value)
                                  compositeRows = compositeData.compositeTargetValues || []
                                } catch {}
                                const handleCompositeValueChange = (
                                  rowIdx: number,
                                  field: keyof CompositeValue,
                                  value: string
                                ) => {
                                  const newRows = compositeRows.map((row, i) =>
                                    i === rowIdx ? { ...row, [field]: value } : row
                                  )
                                  const newCompositeData = { ...compositeData, compositeTargetValues: newRows }
                                  // Update the reviewTargets state
                                  const updated = [...reviewTargets]
                                  updated[index].value = JSON.stringify(newCompositeData)
                                  setReviewTargets(updated)
                                }
                                return (
                                  <Paper elevation={0} sx={{ p: 3, backgroundColor: theme.palette.grey[100] }}>
                                    <Box sx={{ mt: 2, mb: 2 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell width="20%">Label</TableCell>
                                            <TableCell width="40%">Description</TableCell>
                                            <TableCell width="15%">Type</TableCell>
                                            <TableCell width="25%">Value</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {compositeRows.length > 0 ? (
                                            compositeRows.map((item, i) => (
                                              <TableRow key={i}>
                                                <TableCell>
                                                  <TextField
                                                    value={item.label || ''}
                                                    onChange={(e) =>
                                                      handleCompositeValueChange(i, 'label', e.target.value)
                                                    }
                                                    fullWidth
                                                    variant="outlined"
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  <TextField
                                                    value={item.description || ''}
                                                    onChange={(e) =>
                                                      handleCompositeValueChange(i, 'description', e.target.value)
                                                    }
                                                    fullWidth
                                                    variant="outlined"
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {getTypeDisplayName(item.componentType || targetMeta?.targetFormat)}
                                                </TableCell>
                                                <TableCell>
                                                  <ComponentField
                                                    componentType={item.componentType || targetMeta?.targetFormat}
                                                    label=""
                                                    value={item.target || ''}
                                                    onChange={(value) => handleCompositeValueChange(i, 'target', value)}
                                                    name={`composite-row-value-${i}`}
                                                    currency={orgSettings.currency}
                                                    currencyDecimals={orgSettings.currencyDecimals}
                                                    decimalPlaces={targetMeta?.precision ?? 2}
                                                    style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            ))
                                          ) : (
                                            <TableRow>
                                              <TableCell colSpan={4} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                  No composite values.
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Paper>
                                )
                              })()}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    )
  }

  return (
    <>
      {!isReviewMode && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h3">Configure Targets</Typography>
            <Button variant="contained" color="error" onClick={handleClear} size="medium" sx={{ minWidth: '80px' }}>
              Clear
            </Button>
          </Box>
          {renderOrgPeriodConfig()}
          {renderTargetMetaConfig()}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/admin/components/targets')
                    })
                  }}
                  disabled={isReviewLoading || isReviewClicked}
                >
                  Cancel
                </Button>
              </AnimateButton>
              <AnimateButton>
                <Button
                  id="review-targets-button"
                  variant="contained"
                  onClick={handleReview}
                  disabled={
                    isLoadingCoreSettings ||
                    isReviewLoading ||
                    isReviewClicked ||
                    !selectedOrganization ||
                    selectedPeriods.length === 0 ||
                    !isTargetMetasValid()
                  }
                >
                  {isReviewLoading ? <CircularProgress size={24} color="inherit" /> : 'Review'}
                </Button>
              </AnimateButton>
            </Stack>
          </Box>
        </>
      )}
      {isReviewMode && (
        <>
          {renderReviewConfig(expandedRows, setExpandedRows)}
          <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    startTransition(() => {
                      navigate('/admin/components/targets')
                    })
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleBackFromReview}>
                Back
              </Button>
              <Button
                id="create-targets-button"
                variant="contained"
                onClick={handleCreateTargets}
                disabled={isLoadingCoreSettings}
              >
                Create Targets
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </>
  )
}

export default CreateTargets
